import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { tvShows, categories, articles } from '@/mocks/tv-data';
import type { TvShow } from '@/mocks/tv-data';

function HeroBanner() {
  const hotShows = tvShows.filter((s) => s.isHot).slice(0, 4);
  const [current, setCurrent] = useState(0);

  const next = useCallback(() => {
    setCurrent((prev) => (prev + 1) % hotShows.length);
  }, [hotShows.length]);

  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const show = hotShows[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <img
        src={show.bannerImage}
        alt={show.title}
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-50"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 w-full">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-3">
              {show.isNew && (
                <span className="px-2.5 py-1 rounded-full bg-accent-500 text-white text-xs font-medium whitespace-nowrap">
                  NEW
                </span>
              )}
              <span className="px-2.5 py-1 rounded-full bg-primary-500/20 border border-primary-500/30 text-primary-400 text-xs font-medium whitespace-nowrap">
                {show.status}
              </span>
              <span className="text-sm text-foreground-300">{show.year}</span>
              <span className="text-sm text-primary-400 font-semibold">{show.rating}分</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3">
              {show.title}
            </h1>
            <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-4 line-clamp-2 md:line-clamp-3">
              {show.description}
            </p>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {show.genre.map((g) => (
                <span
                  key={g}
                  className="px-2.5 py-1 rounded-full bg-white/10 text-white text-xs whitespace-nowrap"
                >
                  {g === 'costume' ? '古装' : g === 'suspense' ? '悬疑' : g === 'romance' ? '爱情' : g === 'action' ? '动作' : g === 'comedy' ? '喜剧' : g === 'urban' ? '都市' : g === 'fantasy' ? '奇幻' : g}
                </span>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <Link
                to={`/article/${show.id}`}
                className="px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                立即观看
              </Link>
              <span className="text-sm text-foreground-400">
                {show.currentEpisode}/{show.episodeCount}集
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {hotShows.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
              i === current ? 'w-6 bg-primary-500' : 'bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`切换到第${i + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-6 -mt-8 relative z-10">
      <div className="bg-background-100/90 backdrop-blur-md rounded-2xl border border-background-200/60 p-4 md:p-5">
        <div className="flex items-center gap-3 md:gap-5 justify-center flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-background-200/50 hover:bg-primary-500/10 border border-background-300/40 hover:border-primary-500/30 text-foreground-400 hover:text-primary-500 transition-all cursor-pointer whitespace-nowrap group"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${cat.icon} text-lg group-hover:text-primary-500 transition-colors`}></i>
              </div>
              <span className="text-sm font-medium">{cat.name}</span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ShowCard({ show }: { show: TvShow }) {
  return (
    <Link
      to={`/article/${show.id}`}
      className="group cursor-pointer block"
    >
      <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
        <img
          src={show.coverImage}
          alt={show.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        <div className="absolute top-2 left-2 flex items-center gap-1.5">
          {show.isNew && (
            <span className="px-2 py-0.5 rounded-md bg-accent-500 text-white text-[10px] font-bold whitespace-nowrap">
              NEW
            </span>
          )}
          {show.isHot && (
            <span className="px-2 py-0.5 rounded-md bg-primary-500 text-white text-[10px] font-bold whitespace-nowrap">
              HOT
            </span>
          )}
        </div>
        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/60 text-primary-400 text-xs font-semibold">
          {show.rating}
        </div>
        <div className="absolute bottom-2 left-2">
          <span className="text-[11px] text-white/80 bg-black/50 px-2 py-0.5 rounded-md whitespace-nowrap">
            {show.currentEpisode}/{show.episodeCount}集
          </span>
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
        {show.title}
      </h3>
      <p className="text-xs text-foreground-500 line-clamp-1">{show.cast.slice(0, 3).join(' / ')}</p>
    </Link>
  );
}

function HotSection() {
  const hotShows = tvShows.filter((s) => s.isHot);
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-fire-line text-xl text-accent-500"></i>
          </div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100">
            热门推荐
          </h2>
        </div>
        <Link
          to="/category/all"
          className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
        >
          查看更多
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line"></i>
          </div>
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
        {hotShows.slice(0, 12).map((show) => (
          <ShowCard key={show.id} show={show} />
        ))}
      </div>
    </section>
  );
}

function NewSection() {
  const newShows = tvShows.filter((s) => s.isNew);
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-sparkling-line text-xl text-secondary-500"></i>
          </div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100">
            最新上线
          </h2>
        </div>
        <Link
          to="/category/all"
          className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
        >
          查看更多
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line"></i>
          </div>
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
        {newShows.map((show) => (
          <ShowCard key={show.id} show={show} />
        ))}
      </div>
    </section>
  );
}

function ByCategorySection() {
  const cats = categories.filter((c) => c.slug !== 'all');
  return (
    <>
      {cats.map((cat) => {
        const shows = tvShows.filter((s) => s.category === cat.slug).slice(0, 6);
        if (shows.length === 0) return null;
        return (
          <section key={cat.slug} className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center">
                  <i className={`${cat.icon} text-xl text-foreground-300`}></i>
                </div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100">
                  {cat.name}
                </h2>
              </div>
              <Link
                to={`/category/${cat.slug}`}
                className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                更多{cat.name}
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line"></i>
                </div>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {shows.map((show) => (
                <ShowCard key={show.id} show={show} />
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

function ArticleSection() {
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-article-line text-xl text-foreground-300"></i>
          </div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-100">
            影视资讯
          </h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {articles.map((article) => (
          <Link
            key={article.id}
            to={`/article/${article.id}`}
            className="group cursor-pointer bg-background-100 rounded-xl border border-background-200/60 overflow-hidden hover:border-primary-500/30 transition-all"
          >
            <div className="aspect-[16/10] overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
              />
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="px-2 py-0.5 rounded-md bg-primary-500/10 text-primary-400 text-[10px] font-medium whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-[11px] text-foreground-600">{article.publishDate}</span>
              </div>
              <h3 className="text-sm font-medium text-foreground-200 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                {article.title}
              </h3>
              <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed">
                {article.summary}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function StatsBanner() {
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
      <div className="bg-background-100 rounded-2xl border border-background-200/60 p-8 md:p-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">16+</div>
            <p className="text-sm text-foreground-500">热门影视</p>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-heading font-bold text-accent-500 mb-1">500万+</div>
            <p className="text-sm text-foreground-500">日均访问</p>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-heading font-bold text-secondary-500 mb-1">99.9%</div>
            <p className="text-sm text-foreground-500">高清流畅</p>
          </div>
          <div>
            <div className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-1">完全免费</div>
            <p className="text-sm text-foreground-500">无需会员</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <HeroBanner />
        <CategoryNav />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              name: '51影视',
              url: 'https://www.51yingshi.com',
              description: '51影视网提供电视剧全集免费在线观看，最新热播剧免费看，高清不卡顿，畅享追剧体验。',
              potentialAction: {
                '@type': 'SearchAction',
                target: 'https://www.51yingshi.com/search?q={search_term_string}',
                'query-input': 'required name=search_term_string',
              },
            }),
          }}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: '51影视',
              url: 'https://www.51yingshi.com',
              description: '提供高清免费影视在线观看的平台',
              sameAs: [],
            }),
          }}
        />

        <HotSection />
        <StatsBanner />
        <NewSection />
        <ByCategorySection />
        <ArticleSection />
      </main>
      <Footer />
    </div>
  );
}